import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  getToken,
  decodeToken,
  isTokenExpired,
  clearAuthStorage,
  forceLogout,
  attachAuthInterceptors,
  AUTH_LOGOUT_EVENT,
} from './utils/authToken';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api') + '/auth';

// Auto-logout after this much inactivity (no mouse/keyboard/scroll/touch).
const INACTIVITY_LIMIT_MS = 30 * 60 * 1000; // 30 minutes
// How often we proactively check the token's expiry, even with no API calls.
const EXPIRY_CHECK_INTERVAL_MS = 30 * 1000; // 30 seconds

// Install auth token + auto-logout handling on the global axios instance once,
// so every module that uses `import axios from "axios"` is covered too.
let globalAxiosPatched = false;
if (!globalAxiosPatched) {
  globalAxiosPatched = true;
  attachAuthInterceptors(axios);
}

const AuthContext = createContext();

// Compute the logged-in user synchronously from a valid, non-expired token.
// Doing this at initial render (instead of in an effect) prevents a first-render
// flash of "logged out" that would otherwise bounce refreshed users on
// protected pages straight to the sign-in screen.
const getInitialUser = () => {
  const token = getToken();
  if (!token || isTokenExpired(token)) return null;
  const payload = decodeToken(token);
  return payload ? { username: payload.username, email: payload.email } : null;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getInitialUser);
  const [blacklist, setBlacklist] = useState([]);
  const inactivityTimerRef = useRef(null);

  // Fetch user's blacklist from backend
  const fetchBlacklist = useCallback(async (token) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_BASE.replace('/auth', '')}/user/blacklist`, { headers });
      setBlacklist(response.data.blacklist || []);
    } catch (err) {
      console.error('Failed to fetch blacklist:', err);
      setBlacklist([]);
    }
  }, []);

  // Read the stored token; only treat the user as logged in if it's valid AND
  // not expired. An expired token is cleared immediately.
  const syncUser = useCallback(() => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setBlacklist([]);
      return;
    }
    if (isTokenExpired(token)) {
      clearAuthStorage();
      setUser(null);
      setBlacklist([]);
      return;
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setUser({ username: payload.username, email: payload.email });
      fetchBlacklist(token);
    } catch (error) {
      console.error('Failed to parse token:', error);
      clearAuthStorage();
      setUser(null);
      setBlacklist([]);
    }
  }, [fetchBlacklist]);

  // Clears everything locally. `forceLogout` (from interceptors/inactivity) also
  // dispatches the event this provider listens to, so state stays in sync.
  const logout = useCallback(() => {
    clearAuthStorage();
    setUser(null);
    setBlacklist([]);
  }, []);

  // Initial load + cross-tab sync (storage event fires on other tabs).
  useEffect(() => {
    syncUser();
    window.addEventListener('storage', syncUser);
    return () => window.removeEventListener('storage', syncUser);
  }, [syncUser]);

  // React to global logout events (expired token, 401 response, inactivity).
  useEffect(() => {
    const onLogout = () => {
      setUser(null);
      setBlacklist([]);
    };
    window.addEventListener(AUTH_LOGOUT_EVENT, onLogout);
    return () => window.removeEventListener(AUTH_LOGOUT_EVENT, onLogout);
  }, []);

  // Proactively log out when the token expires, even if the user is idle and
  // never triggers an API call.
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      if (isTokenExpired(getToken())) {
        forceLogout('expired');
      }
    }, EXPIRY_CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user]);

  // Inactivity auto-logout — reset the timer on any real user activity.
  useEffect(() => {
    if (!user) return;

    const resetTimer = () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = setTimeout(() => {
        forceLogout('inactive');
      }, INACTIVITY_LIMIT_MS);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((e) => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      events.forEach((e) => window.removeEventListener(e, resetTimer));
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  }, [user]);

  // Add game to blacklist
  const addToBlacklist = async (gameId) => {
    try {
      const token = getToken();
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_BASE.replace('/auth', '')}/user/blacklist`, { gameId }, { headers });
      setBlacklist(prev => [...prev, gameId]);
    } catch (err) {
      console.error('Failed to add to blacklist:', err);
    }
  };

  // Remove game from blacklist
  const removeFromBlacklist = async (gameId) => {
    try {
      const token = getToken();
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API_BASE.replace('/auth', '')}/user/blacklist/${gameId}`, { headers });
      setBlacklist(prev => prev.filter(id => id !== gameId));
    } catch (err) {
      console.error('Failed to remove from blacklist:', err);
    }
  };

  // Check if game is blacklisted
  const isBlacklisted = (gameId) => {
    return blacklist.includes(gameId);
  };

  // Update user profile helper: tries server endpoint, falls back to updating local state
  const updateUserProfile = async (profile) => {
    // Try server-side update if endpoint exists
    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // Attempt PATCH to /auth/update (may be missing on some deployments)
      const resp = await axios.patch(`${API_BASE}/update`, profile, { headers });
      // If server returns a new token or user, sync it
      if (resp?.data?.token) {
        const newToken = resp.data.token;
        // Store token in localStorage (keep same storage pattern as login)
        localStorage.setItem('authToken', newToken);
        const payload = JSON.parse(atob(newToken.split('.')[1]));
        const newUser = { username: payload.username, email: payload.email };
        setUser(newUser);
        return newUser;
      }
      // If no token returned but server responded OK, update local user fields
      if (resp?.status === 200 || resp?.status === 204) {
        setUser(prev => ({ ...(prev || {}), ...(profile || {}) }));
        return { ...(user || {}), ...(profile || {}) };
      }
    } catch (err) {
      // Endpoint may not exist or failed; fall back to local update
      setUser(prev => ({ ...(prev || {}), ...(profile || {}) }));
      return { ...(user || {}), ...(profile || {}) };
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, logout, updateUserProfile, blacklist, addToBlacklist, removeFromBlacklist, isBlacklisted }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
